import { useEffect, useId, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import PageBackBar from "../components/PageBackBar";
import { useAuth } from "../features/auth/useAuth";
import { exportActiveLocationUsersCsv } from "../features/directory/api";
import { activationPercent, isActivationChecked } from "../features/directory/activation";
import ActivationSummaryCard from "../features/directory/ActivationSummaryCard";
import LoggedInInfo from "../features/directory/LoggedInInfo";
import {
  DEFAULT_PAGE_SIZE,
  DirectoryPagination,
  PageSizeSelect
} from "../features/directory/DirectoryPagination";
import {
  extractAxiosDetail,
  formatAbsolute,
  formatDate,
  formatRelative,
  fullName,
  humanizeToken
} from "../features/directory/format";
import ConfirmDialog from "../features/directory/ConfirmDialog";
import {
  useDeactivateLocationUser,
  useDebouncedSearch,
  useLocationUsers,
  useWorkvivoDirectoryRefresh
} from "../features/directory/hooks";
import type { LocationUser, LocationUsersStatusFilter, LocationUsersStore } from "../features/directory/types";
import WorkvivoSyncBar from "../features/directory/WorkvivoSyncBar";
import "../App.css";

const STATUS_OPTIONS: { value: LocationUsersStatusFilter; label: string; activation?: boolean }[] = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "deactivated", label: "Deactivated" },
  { value: "activated", label: "Has logged in", activation: true },
  { value: "not_activated", label: "Not logged in yet", activation: true }
];

const ACTIVATION_STATUSES: LocationUsersStatusFilter[] = ["activated", "not_activated"];

function StatusSegmented({
  value,
  onChange,
  showActivation
}: {
  value: LocationUsersStatusFilter;
  onChange: (value: LocationUsersStatusFilter) => void;
  /** Login filters only make sense once Workvivo logins have been checked. */
  showActivation: boolean;
}) {
  const options = STATUS_OPTIONS.filter((opt) => showActivation || !opt.activation);
  return (
    <div className="directory-segmented" role="group" aria-label="Filter by Workvivo status">
      <span className="directory-segmented-label">Status</span>
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          aria-pressed={value === opt.value}
          data-active={value === opt.value || undefined}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

function RoleCell({ u }: { u: LocationUser }) {
  const hasChips = Boolean(u.employee_type) || Boolean(u.group_name);
  return (
    <div className="directory-stack">
      <span className={u.role ? "directory-cell-text" : "directory-muted"}>{u.role || "—"}</span>
      {u.department ? <span className="directory-muted-small">{u.department}</span> : null}
      {hasChips ? (
        <span className="directory-badge-row">
          {u.employee_type ? <span className="directory-tag">{humanizeToken(u.employee_type)}</span> : null}
          {u.group_name ? <span className="directory-tag directory-tag--role">{u.group_name}</span> : null}
        </span>
      ) : null}
    </div>
  );
}

function LocationCell({ u }: { u: LocationUser }) {
  if (u.store_name) {
    return (
      <div className="directory-stack">
        <span className="directory-cell-text">{u.store_name}</span>
        {u.store_id ? <span className="directory-muted-small">{u.store_id}</span> : null}
      </div>
    );
  }
  return (
    <div className="directory-stack">
      <span className={u.locality ? "directory-cell-text" : "directory-muted"}>{u.locality || "—"}</span>
    </div>
  );
}

function StatusCell({ u }: { u: LocationUser }) {
  return (
    <div className="directory-stack">
      <span className={`directory-badge ${u.active ? "directory-badge--ok" : "directory-badge--inactive"}`}>
        {u.active ? "Active in Workvivo" : "Deactivated in Workvivo"}
      </span>
      {u.wv_modified_at ? (
        <span className="directory-muted-small">Updated in Workvivo {formatRelative(u.wv_modified_at)}</span>
      ) : null}
    </div>
  );
}

/** Signed in to Workvivo at least once? Only meaningful for people active in Workvivo. */
function ActivationCell({ u }: { u: LocationUser }) {
  if (!u.active) {
    return (
      <span className="directory-muted" title="Not applicable to deactivated users">
        —
      </span>
    );
  }
  if (u.has_logged_in === true) {
    const since = formatDate(u.activated_seen_at);
    return (
      <div className="directory-stack">
        <span className="directory-badge directory-badge--ok">Has logged in</span>
        {since ? (
          <span className="directory-muted-small" title={formatAbsolute(u.activated_seen_at)}>
            since {since}
          </span>
        ) : null}
      </div>
    );
  }
  if (u.has_logged_in === false) {
    const added = formatRelative(u.wv_created_at);
    return (
      <div className="directory-stack">
        <span className="directory-badge directory-badge--warn">Not logged in yet</span>
        {added ? (
          <span className="directory-muted-small" title={formatAbsolute(u.wv_created_at)}>
            Added {added}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <div className="directory-stack">
      <span className="directory-badge directory-badge--muted">Unknown</span>
      <span className="directory-muted-small">Not checked yet</span>
    </div>
  );
}

/** "74% logged in" for a location tab (same maths as the donut), or null when nobody there has been checked. */
function storeActivationLabel(s: LocationUsersStore): string | null {
  const checked = (s.activated_count ?? 0) + (s.not_activated_count ?? 0);
  if (checked <= 0) return null;
  const pct = activationPercent(s.activated_count ?? 0, s.active_count);
  return pct === null ? null : `${pct}% logged in`;
}

function LocationUsersView({
  ownerId,
  isSuperuser,
  initialStoreId
}: {
  ownerId?: string;
  isSuperuser: boolean;
  /** Preselected location tab (from `?store=`); falls back to all locations if it isn't one of theirs. */
  initialStoreId?: string;
}) {
  const searchId = useId();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [status, setStatus] = useState<LocationUsersStatusFilter>("all");
  const [storeId, setStoreId] = useState<string | null>(initialStoreId || null);
  const search = useDebouncedSearch(300, () => setPage(0));
  const workvivoRefresh = useWorkvivoDirectoryRefresh();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [deactivateFor, setDeactivateFor] = useState<LocationUser | null>(null);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const deactivate = useDeactivateLocationUser();

  const query = useLocationUsers({
    ownerId,
    storeId: storeId ?? undefined,
    q: search.query || undefined,
    status,
    limit: pageSize,
    offset: page * pageSize
  });

  const data = query.data;
  const stores = data?.stores ?? [];

  useEffect(() => {
    const d = query.data;
    if (query.isFetching || !d) return;
    if (d.total > 0 && d.items.length === 0 && d.offset >= d.total) {
      setPage(0);
    }
    // The selected location is no longer part of this account (e.g. reassigned): fall back to all.
    if (storeId && !d.stores.some((s) => s.store_id === storeId)) {
      setStoreId(null);
      setPage(0);
    }
    // Activation filters disappear when activation has never been checked: fall back to all.
    if (!isActivationChecked(d.activation_sync) && ACTIVATION_STATUSES.includes(status)) {
      setStatus("all");
      setPage(0);
    }
  }, [query.data, query.isFetching]);

  const changePageSize = (n: number) => {
    setPageSize(n);
    setPage(0);
  };

  const changeStatus = (next: LocationUsersStatusFilter) => {
    setStatus(next);
    setPage(0);
  };

  const ownerName = data ? fullName(data.owner.first_name, data.owner.last_name, data.owner.email) : null;
  const title = isSuperuser ? `Users managed by ${ownerName ?? "…"}` : "My location users";
  const allCount = stores.reduce((sum, s) => sum + (s.user_count ?? 0), 0);
  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const offset = data?.offset ?? 0;
  const noStores = Boolean(data) && stores.length === 0;
  const selectedStore = storeId ? stores.find((s) => s.store_id === storeId) : undefined;
  const allUnmapped = stores.length > 0 && stores.every((s) => !s.mapped);
  const neverLoaded = data?.sync?.status === "never";
  const activationChecked = isActivationChecked(data?.activation_sync);

  const handleExport = async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      await exportActiveLocationUsersCsv({
        ownerId,
        storeId: storeId ?? undefined,
        q: search.query || undefined
      });
    } catch (e) {
      setExportError(extractAxiosDetail(e));
    } finally {
      setIsExporting(false);
    }
  };

  let emptyTitle = "No users in your locations yet";
  let emptyDescription =
    "Users appear here once they are provisioned by CSV upload or approved through self-registration.";
  if (neverLoaded && total === 0 && !search.query) {
    emptyTitle = "Workvivo data hasn't been loaded yet";
    emptyDescription =
      "We're fetching the Workvivo directory now — this can take a minute for large teams. Users will appear here once the first sync finishes.";
  } else if (allUnmapped) {
    emptyTitle = "These locations aren't mapped to Workvivo";
    emptyDescription =
      "None of these locations have a catalog location set, so their Workvivo users can't be matched. Set a catalog location for each store to see its people here.";
  } else if (search.query) {
    emptyTitle = "No users match your search";
    emptyDescription = `Nothing matches “${search.query}”${
      selectedStore ? ` in ${selectedStore.business_name}` : ""
    }. Try a name, email or role.`;
  } else if (selectedStore) {
    if (status === "activated") {
      emptyTitle = `Nobody at ${selectedStore.business_name} has logged in to Workvivo yet`;
      emptyDescription = `None of the active people at ${selectedStore.business_name} (${selectedStore.store_id}) have signed in to Workvivo yet.`;
    } else if (status === "not_activated") {
      emptyTitle = `Everyone active at ${selectedStore.business_name} has logged in to Workvivo`;
      emptyDescription = `Every active person we could check at ${selectedStore.business_name} (${selectedStore.store_id}) has signed in to Workvivo at least once.`;
    } else if (status === "deactivated") {
      emptyTitle = "No deactivated users in this location";
      emptyDescription = `${selectedStore.business_name} (${selectedStore.store_id}) has no users deactivated in Workvivo.`;
    } else if (status === "active") {
      emptyTitle = "No active users in this location";
      emptyDescription = `${selectedStore.business_name} (${selectedStore.store_id}) has no users active in Workvivo.`;
    } else {
      emptyTitle = "No users in this location yet";
      emptyDescription = `${selectedStore.business_name} (${selectedStore.store_id}) has no matched Workvivo users.`;
    }
  } else if (status === "activated") {
    emptyTitle = "Nobody in these locations has logged in to Workvivo yet";
    emptyDescription =
      "None of the active people in these locations have signed in to Workvivo yet. Switch to “All” to see everyone.";
  } else if (status === "not_activated") {
    emptyTitle = "Everyone active in these locations has logged in to Workvivo";
    emptyDescription =
      "Every active person we could check has signed in to Workvivo at least once. Switch to “All” to see everyone.";
  } else if (status === "deactivated") {
    emptyTitle = "No deactivated users";
    emptyDescription = "Switch to “All” to see every user regardless of Workvivo status.";
  } else if (status === "active") {
    emptyTitle = "No active users";
    emptyDescription = "Switch to “All” to see every user regardless of Workvivo status.";
  } else if (isSuperuser) {
    emptyTitle = "No users in this admin’s locations yet";
  }

  return (
    <div className="directory-page">
      {isSuperuser ? <PageBackBar to="/location-admins" label="Back to location admins" /> : <PageBackBar />}
      <header>
        <h1 className="approvals-page-title">{title}</h1>
        <p className="approvals-page-sub">
          {isSuperuser ? (
            <>
              Users provisioned in the locations managed by{" "}
              <strong style={{ color: "#334155" }}>{data?.owner.email ?? "this Location Manager"}</strong>. Status
              comes from the cached Workvivo directory.
            </>
          ) : (
            "Users provisioned in the locations you manage. Active/deactivated status comes from Workvivo, the source of truth."
          )}
        </p>
      </header>

      {data ? <WorkvivoSyncBar sync={data.sync} refresh={workvivoRefresh} /> : null}

      {query.isLoading ? (
        <div className="directory-state" role="status">
          Loading users…
        </div>
      ) : query.isError && !data ? (
        <div className="directory-error" role="alert">
          <strong>Failed to load users.</strong> {extractAxiosDetail(query.error)}
          {isSuperuser ? (
            <div style={{ marginTop: "0.5rem" }}>
              <Link to="/location-admins" className="directory-action-link">
                Choose a location admin <span aria-hidden>→</span>
              </Link>
            </div>
          ) : null}
        </div>
      ) : noStores ? (
        <EmptyState
          title={
            isSuperuser
              ? "No locations are assigned to this admin yet"
              : "No locations are assigned to this account yet"
          }
          description={
            isSuperuser
              ? "Once locations are linked to this Location Manager, their users will show up here."
              : "Once a superuser links locations to your account, the users provisioned there will show up here."
          }
          action={
            isSuperuser ? (
              <Link to="/location-admins" className="app-header-link-outline">
                Back to location admins
              </Link>
            ) : undefined
          }
        />
      ) : (
        <>
          <div className="approvals-tab-strip directory-tab-strip" role="tablist" aria-label="Filter by location">
            <button
              type="button"
              role="tab"
              aria-selected={storeId === null}
              data-active={storeId === null}
              onClick={() => {
                setStoreId(null);
                setPage(0);
              }}
            >
              All locations ({allCount})
            </button>
            {stores.map((s) => {
              const activationLabel = activationChecked ? storeActivationLabel(s) : null;
              return (
                <button
                  key={s.store_id}
                  type="button"
                  role="tab"
                  aria-selected={storeId === s.store_id}
                  data-active={storeId === s.store_id}
                  data-inactive={!s.is_active || undefined}
                  title={s.is_active ? `${s.business_name} · ${s.store_id}` : `${s.business_name} · ${s.store_id} (inactive location)`}
                  onClick={() => {
                    setStoreId(s.store_id);
                    setPage(0);
                  }}
                >
                  {s.business_name} ({s.user_count})
                  {s.mapped ? null : (
                    <span
                      className="directory-unmapped-marker"
                      title="This location has no catalog location, so its Workvivo users cannot be matched."
                    >
                      <span aria-hidden>⚠</span> no catalog location
                    </span>
                  )}
                  {s.is_active ? null : (
                    <span className="directory-tab-inactive">
                      <span aria-hidden> · </span>inactive
                    </span>
                  )}
                  {activationLabel ? (
                    <span className="directory-activation-tab-rate">
                      <span aria-hidden> · </span>
                      {activationLabel}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>

          {data && activationChecked ? (
            <ActivationSummaryCard
              counts={data.summary}
              finishedAt={data.activation_sync.finished_at}
              label={selectedStore ? `Workvivo logins for ${selectedStore.business_name}` : "Workvivo logins"}
            />
          ) : data ? (
            <p className="directory-activation-note">{"Workvivo logins haven't been checked yet."}</p>
          ) : null}

          <div className="directory-controls" role="search">
            <label htmlFor={searchId} className="visually-hidden">
              Search users
            </label>
            <input
              id={searchId}
              type="search"
              className="directory-search"
              placeholder="Search by name, email or role"
              value={search.input}
              onChange={(e) => search.setInput(e.target.value)}
              autoComplete="off"
            />
            <StatusSegmented value={status} onChange={changeStatus} showActivation={activationChecked} />
            <PageSizeSelect value={pageSize} onChange={changePageSize} />
            {query.isFetching ? (
              <span className="directory-updating" role="status">
                Updating…
              </span>
            ) : null}
            <button
              type="button"
              className="directory-action-link directory-export-btn"
              onClick={() => void handleExport()}
              disabled={isExporting || neverLoaded}
              title="Download a CSV of active Workvivo users in these locations"
            >
              <span aria-hidden>⬇</span> {isExporting ? "Exporting…" : "Export active users"}
            </button>
          </div>

          {notice ? (
            <div className="directory-notice" role="status">
              <span>{notice}</span>
              <button type="button" className="directory-link-btn" onClick={() => setNotice(null)}>
                Dismiss
              </button>
            </div>
          ) : null}

          {exportError ? (
            <div className="directory-error" role="alert">
              <strong>Failed to export users.</strong> {exportError}
            </div>
          ) : null}

          {query.isError ? (
            <div className="directory-error" role="alert">
              <strong>Failed to refresh users.</strong> {extractAxiosDetail(query.error)}
            </div>
          ) : total === 0 ? (
            <EmptyState title={emptyTitle} description={emptyDescription} />
          ) : (
            <>
              <div
                className="directory-table-wrap"
                aria-busy={query.isPlaceholderData}
                data-stale={query.isPlaceholderData || undefined}
              >
                <table className="directory-table">
                  <thead>
                    <tr>
                      <th scope="col">User</th>
                      <th scope="col">Location</th>
                      <th scope="col">Role</th>
                      <th scope="col">Status</th>
                      {activationChecked ? (
                        <th scope="col">
                          Workvivo login
                          <LoggedInInfo />
                        </th>
                      ) : null}
                      <th scope="col">Last checked</th>
                      <th scope="col">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((u) => {
                      const personName = u.display_name || fullName(u.first_name, u.last_name, u.email);
                      return (
                      <tr key={u.workvivo_id}>
                        <td data-label="User" className="directory-cell-primary">
                          <div className="directory-person">
                            <span className="directory-person-name">{personName}</span>
                            <span className="directory-person-email">{u.email}</span>
                          </div>
                        </td>
                        <td data-label="Location">
                          <LocationCell u={u} />
                        </td>
                        <td data-label="Role">
                          <RoleCell u={u} />
                        </td>
                        <td data-label="Status">
                          <StatusCell u={u} />
                        </td>
                        {activationChecked ? (
                          <td data-label="Workvivo login">
                            <ActivationCell u={u} />
                          </td>
                        ) : null}
                        <td data-label="Last checked">
                          <span title={formatAbsolute(u.fetched_at)}>{formatRelative(u.fetched_at) || "—"}</span>
                        </td>
                        <td className="directory-cell-action">
                          {u.active ? (
                            <button
                              type="button"
                              className="directory-btn directory-btn--danger directory-btn--small"
                              aria-label={`Deactivate ${personName} in Workvivo`}
                              onClick={() => {
                                setDeactivateError(null);
                                setDeactivateFor(u);
                              }}
                            >
                              Deactivate
                            </button>
                          ) : null}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <DirectoryPagination
                page={page}
                pageSize={pageSize}
                total={total}
                offset={offset}
                itemCount={items.length}
                noun="users"
                onPageChange={setPage}
                onPageSizeChange={changePageSize}
              />
            </>
          )}
        </>
      )}

      <ConfirmDialog
        open={deactivateFor != null}
        danger
        title="Deactivate in Workvivo"
        body={
          deactivateFor ? (
            <>
              <p>
                This deactivates{" "}
                <strong>
                  {deactivateFor.display_name ||
                    fullName(deactivateFor.first_name, deactivateFor.last_name, deactivateFor.email)}
                </strong>{" "}
                ({deactivateFor.email}) in Workvivo immediately. They will lose access right away.
              </p>
              <p>
                <strong>To reactivate them later,</strong> upload them again in a CSV with{" "}
                <strong>is_active</strong> set to <strong>true</strong>, or ask a Workvivo administrator.
              </p>
            </>
          ) : null
        }
        requireText={deactivateFor?.email ?? ""}
        requireTextLabel={
          <>
            Type the person’s email <strong>{deactivateFor?.email}</strong> to confirm
          </>
        }
        confirmLabel="Deactivate"
        isPending={deactivate.isPending}
        error={deactivateError}
        onCancel={() => {
          setDeactivateFor(null);
          setDeactivateError(null);
        }}
        onConfirm={async () => {
          if (!deactivateFor) return;
          setDeactivateError(null);
          try {
            // The dialog only enables Confirm once the typed text matches (case-insensitively);
            // send the canonical email so a server-side exact compare can't fail on casing.
            await deactivate.mutateAsync({
              workvivoId: deactivateFor.workvivo_id,
              confirmEmail: deactivateFor.email
            });
            setNotice(`${deactivateFor.email} was deactivated in Workvivo.`);
            setDeactivateFor(null);
          } catch (e) {
            setDeactivateError(extractAxiosDetail(e));
          }
        }}
      />
    </div>
  );
}

export default function LocationUsers() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const role = user?.role;
  const initialStoreId = searchParams.get("store")?.trim() || undefined;

  if (!user || (role !== "superuser" && role !== "retailer")) {
    return <div style={{ color: "#b91c1c" }}>You don’t have access to view location users.</div>;
  }

  if (role === "superuser") {
    const ownerId = searchParams.get("owner")?.trim();
    if (!ownerId) {
      return (
        <div className="directory-page">
          <PageBackBar to="/location-admins" label="Back to location admins" />
          <header>
            <h1 className="approvals-page-title">Location users</h1>
          </header>
          <EmptyState
            title="Pick a location admin first"
            description="As a super user you can view the users under any Location Manager. Choose an admin from the directory and select “View users”."
            action={
              <Link to="/location-admins" className="app-header-link-primary">
                Go to location admins
              </Link>
            }
          />
        </div>
      );
    }
    // Keyed by owner (and drill-down store) so filters / paging reset cleanly when switching admins.
    return (
      <LocationUsersView
        key={`${ownerId}|${initialStoreId ?? ""}`}
        ownerId={ownerId}
        isSuperuser
        initialStoreId={initialStoreId}
      />
    );
  }

  return <LocationUsersView key={initialStoreId ?? ""} isSuperuser={false} initialStoreId={initialStoreId} />;
}
